import { usePageContext } from "vike-react/usePageContext";

// ─── Language content ────────────────────────────────────────────────────────

type LanguageData = {
  name: string;
  nativeName: string;
  speakers: string;
  countries: string;
  flag: string;
  difficulty: string;
  timeToB2: string;
  tagline: string;
  facts: string[];
  whyLearn: string[];
};

const languageData: Record<string, LanguageData> = {
  es: {
    name: "Spanish",
    nativeName: "Español",
    speakers: "500M+",
    countries: "20",
    flag: "🇪🇸",
    difficulty: "Easy for English speakers",
    timeToB2: "~600 hours",
    tagline: "The world's most-studied foreign language",
    facts: [
      "Spanish is the second most spoken language by native speakers, with over 500 million across the Americas and Europe.",
      "It's an official language in 20 countries — more than any other Romance language.",
      "Spanish and English share thousands of cognates (similar words), making vocabulary considerably easier to pick up.",
      "Spanish is the most studied foreign language in the United States, with over 41 million heritage speakers.",
    ],
    whyLearn: [
      "Connect with 500M+ native speakers across 4 continents",
      "Travel freely across Latin America and Spain",
      "Unlock career opportunities in the US, Europe, and Latin America",
      "Access a rich tradition of literature, cinema, and music",
    ],
  },
  pt: {
    name: "Portuguese",
    nativeName: "Português",
    speakers: "250M+",
    countries: "10",
    flag: "🇧🇷",
    difficulty: "Easy for Spanish speakers",
    timeToB2: "~650 hours",
    tagline: "The language of Brazil — and three other continents",
    facts: [
      "Portuguese is the sixth most spoken language in the world, with over 250 million native speakers.",
      "Brazil alone has more than 215 million speakers and is Latin America's largest economy.",
      "Portuguese and Spanish share around 89% lexical similarity — ideal as a second Romance language.",
      "It's the fastest-growing European language after English, particularly in Africa and Asia.",
    ],
    whyLearn: [
      "Access Brazil's growing economy and vibrant culture",
      "Speak with communities across South America, Africa, and Europe",
      "A natural step if you already have some Spanish",
      "Rich literary tradition from Fernando Pessoa to José Saramago",
    ],
  },
  fr: {
    name: "French",
    nativeName: "Français",
    speakers: "300M+",
    countries: "29",
    flag: "🇫🇷",
    difficulty: "Moderate for English speakers",
    timeToB2: "~750 hours",
    tagline: "The language of diplomacy, cuisine, and culture",
    facts: [
      "French is an official language of the United Nations, the EU, NATO, and over 25 international organisations.",
      "More French speakers live in Africa than in France — it's one of the fastest-growing languages by total speakers.",
      "Around 45% of English vocabulary has French or Norman origins, giving English speakers a head start.",
      "French is the second most studied language in the world after English.",
    ],
    whyLearn: [
      "Work across 29 French-speaking countries on 5 continents",
      "Access international organisations and diplomatic careers",
      "Enjoy French cinema, literature, and cuisine more deeply",
      "Strengthen your English vocabulary through shared roots",
    ],
  },
  it: {
    name: "Italian",
    nativeName: "Italiano",
    speakers: "65M+",
    countries: "4",
    flag: "🇮🇹",
    difficulty: "Easy to moderate for English speakers",
    timeToB2: "~600 hours",
    tagline: "The most musical of the Romance languages",
    facts: [
      "Italian is the closest living language to classical Latin, making it the most 'pure' of the Romance family.",
      "It's the official language of music — tempo, forte, piano, soprano, and hundreds of musical terms are Italian.",
      "Italy has more UNESCO World Heritage Sites than any other country in the world.",
      "Italian is the fourth most studied foreign language globally, popular for its culture and sound.",
    ],
    whyLearn: [
      "Experience Italy's art, history, and cuisine at a deeper level",
      "Understand music, opera, and art history more richly",
      "Relatively straightforward grammar compared to French",
      "Gateway to Switzerland, San Marino, and parts of Croatia",
    ],
  },
  ca: {
    name: "Catalan",
    nativeName: "Català",
    speakers: "10M+",
    countries: "3",
    flag: "🏴",
    difficulty: "Easy for Spanish or Italian speakers",
    timeToB2: "~500 hours",
    tagline: "The language of Barcelona — and a proud literary tradition",
    facts: [
      "Catalan has more native speakers than Danish, Finnish, or Norwegian — it's not a dialect of Spanish.",
      "It's co-official with Spanish in Catalonia, Valencia, and the Balearic Islands.",
      "Barcelona, one of Europe's top business and tech hubs, has a strongly Catalan-speaking culture.",
      "Catalan has a medieval literary tradition that predates modern Castilian Spanish prose.",
    ],
    whyLearn: [
      "Connect deeply with Barcelona's business and cultural life",
      "Navigate Catalonia, Valencia, and the Balearic Islands like a local",
      "A natural complement to Spanish for Iberian professionals",
      "Access a distinct cultural and literary heritage",
    ],
  },
  ro: {
    name: "Romanian",
    nativeName: "Română",
    speakers: "25M+",
    countries: "2",
    flag: "🇷🇴",
    difficulty: "Moderate for Romance language speakers",
    timeToB2: "~700 hours",
    tagline: "Eastern Europe's only Romance language",
    facts: [
      "Romanian preserves more Latin vocabulary than any other modern Romance language.",
      "It's the only major Romance language in Eastern Europe, shaped by Slavic, Greek, and Turkish contact.",
      "Unlike other Romance languages, Romanian retains a grammatical case system from Latin.",
      "Romania is an EU member and a growing tech hub with one of Europe's fastest average internet speeds.",
    ],
    whyLearn: [
      "Discover the linguistic bridge between Latin and Slavic Europe",
      "Access Romania and Moldova's growing economies",
      "Romanian speakers often find other Romance languages easier to learn afterwards",
      "Stand out — Romanian is one of the rarest Romance languages to study in the English-speaking world",
    ],
  },
  fi: {
    name: "Finnish",
    nativeName: "Suomi",
    speakers: "5M+",
    countries: "2",
    flag: "🇫🇮",
    difficulty: "Hard for English speakers",
    timeToB2: "~1,100 hours",
    tagline: "One of the world's most logical languages — once you get started",
    facts: [
      "Finnish belongs to the Finno-Ugric family, making it unrelated to most European languages — but that also means it's highly consistent once you know the rules.",
      "Finnish has 15 grammatical cases, but they follow regular patterns far more reliably than the case systems of Slavic languages.",
      "Finland is consistently ranked among the world's top countries for education, quality of life, and digital infrastructure.",
      "Finnish and Estonian are closely related — learning one gives you a significant head start on the other.",
    ],
    whyLearn: [
      "Connect with Finnish culture, literature, and the outdoors",
      "Work in Finland's strong tech and design industries",
      "Finnish grammar is genuinely logical — irregular verbs are rare",
      "A foundation for Estonian and other Finno-Ugric languages",
    ],
  },
};

// ─── Practice sections ────────────────────────────────────────────────────────

const sections = [
  {
    key: "grammar",
    title: "Grammar & Conjugation",
    description:
      "Master verb tenses with spaced repetition. Practice present, past, future, and subjunctive across CEFR levels A1–C2.",
    icon: "📝",
    gradientFrom: "from-blue-50 dark:from-blue-950/30",
    borderHover: "hover:border-blue-300 dark:hover:border-blue-700",
    titleColor: "text-blue-700 dark:text-blue-300",
    path: "grammar",
  },
  {
    key: "vocabulary",
    title: "Vocabulary",
    description:
      "Learn high-frequency words with flashcards and smart review. Build your active word bank from A1 basics to C2 advanced terms.",
    icon: "📚",
    gradientFrom: "from-green-50 dark:from-green-950/30",
    borderHover: "hover:border-green-300 dark:hover:border-green-700",
    titleColor: "text-green-700 dark:text-green-300",
    path: "vocabulary",
  },
  {
    key: "sentences",
    title: "Sentence Building",
    description:
      "Practice constructing sentences with guided fill-in-the-blank exercises. Reinforce grammar and vocabulary in context.",
    icon: "✍️",
    gradientFrom: "from-purple-50 dark:from-purple-950/30",
    borderHover: "hover:border-purple-300 dark:hover:border-purple-700",
    titleColor: "text-purple-700 dark:text-purple-300",
    path: "sentences",
  },
  {
    key: "comprehension",
    title: "Comprehension",
    description:
      "Improve reading and listening with curated texts and exercises matched to your CEFR level.",
    icon: "👁️",
    gradientFrom: "from-orange-50 dark:from-orange-950/30",
    borderHover: "hover:border-orange-300 dark:hover:border-orange-700",
    titleColor: "text-orange-700 dark:text-orange-300",
    path: "comprehension",
  },
  {
    key: "speaking",
    title: "Speaking Practice",
    description:
      "Find tutors, conversation partners, and pronunciation resources to build real-world spoken fluency.",
    icon: "🎤",
    gradientFrom: "from-red-50 dark:from-red-950/30",
    borderHover: "hover:border-red-300 dark:hover:border-red-700",
    titleColor: "text-red-700 dark:text-red-300",
    path: "speaking",
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Page() {
  const pageContext = usePageContext();
  const languageCode = pageContext.routeParams?.language || "es";
  const data = languageData[languageCode] || languageData.es;
  const languageNameLower = data.name.toLowerCase();
  const appUrl = `/learn-${languageNameLower}`;

  const otherLanguages = Object.entries(languageData).filter(
    ([code]) => code !== languageCode
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-4">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="text-center py-8 mb-8">
        <div className="text-6xl mb-4">{data.flag}</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Practice {data.name} Online — Free
        </h1>
        <p className="text-base text-gray-500 dark:text-gray-400 italic mb-4">
          {data.tagline}
        </p>
        <p className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-6">
          Conjugr8 is a free {data.name} practice tool covering grammar,
          vocabulary, sentences, speaking, and comprehension — built around
          spaced repetition so you actually remember what you learn.
        </p>
        <a
          href={appUrl}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-base"
        >
          Start practising {data.name} →
        </a>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span>{data.speakers} native speakers</span>
          <span className="hidden sm:inline">·</span>
          <span>Official in {data.countries} countries</span>
          <span className="hidden sm:inline">·</span>
          <span>{data.difficulty}</span>
          <span className="hidden sm:inline">·</span>
          <span>B2 reachable in {data.timeToB2}</span>
        </div>
      </div>

      {/* ── Practice modes ────────────────────────────────────────────────── */}
      <section className="mb-10" aria-label="Practice modes">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          What you can practise
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {sections.map((section) => (
            <a
              key={section.key}
              href={`${appUrl}/${section.path}`}
              rel="follow index"
              className={`group rounded-xl p-4 border border-gray-200 dark:border-gray-800 bg-gradient-to-br ${section.gradientFrom} to-transparent ${section.borderHover} transition-colors`}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl" aria-hidden="true">
                  {section.icon}
                </span>
                <div>
                  <div className={`font-semibold ${section.titleColor} mb-1`}>
                    {section.title}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {section.description}
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ── Why learn ─────────────────────────────────────────────────────── */}
      <section
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-10"
        aria-label={`Why learn ${data.name}`}
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Why learn {data.name}?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.whyLearn.map((reason, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {reason}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Language facts ────────────────────────────────────────────────── */}
      <section className="mb-10" aria-label={`${data.name} language facts`}>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {data.name} at a glance
        </h2>
        <div className="grid grid-cols-1 gap-2">
          {data.facts.map((fact, i) => (
            <div
              key={i}
              className="flex items-start gap-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4"
            >
              <span className="text-blue-500 font-bold text-sm mt-0.5 flex-shrink-0">
                {i + 1}
              </span>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {fact}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <section
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-10"
        aria-label="How Conjugr8 works"
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          How Conjugr8 works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            {
              step: "1",
              title: "Choose a skill",
              desc: `Pick grammar, vocabulary, sentences, speaking, or comprehension for ${data.name}.`,
            },
            {
              step: "2",
              title: "Practice daily",
              desc: "Short focused sessions with spaced repetition keep words and conjugations in long-term memory.",
            },
            {
              step: "3",
              title: "Track your level",
              desc: "Watch your CEFR level climb from A1 beginner to C2 advanced as exercises are completed.",
            },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-bold text-lg flex items-center justify-center mx-auto mb-3">
                {item.step}
              </div>
              <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">
                {item.title}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CEFR callout ──────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-800 p-6 mb-10">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
          CEFR-structured practice
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Conjugr8 organises all {data.name} exercises around the Common
          European Framework of Reference for Languages (CEFR). Every
          vocabulary set, grammar tense, and sentence exercise is tagged to a
          level — so you always know where you stand and what to study next.
        </p>
        <div className="flex flex-wrap gap-2">
          {["A1", "A2", "B1", "B2", "C1", "C2"].map((level) => (
            <span
              key={level}
              className="px-3 py-1 rounded-full text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
            >
              {level}
            </span>
          ))}
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="text-center bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-8 mb-10">
        <h2 className="text-2xl font-bold text-white mb-2">
          Ready to practise {data.name}?
        </h2>
        <p className="text-blue-100 mb-5 text-sm">
          Free to use. No account needed to get started.
        </p>
        <a
          href={appUrl}
          className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors"
        >
          Open {data.name} practice →
        </a>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="mb-10" aria-label={`${data.name} practice FAQ`}>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Common questions about practising {data.name}
        </h2>
        <dl className="space-y-4">
          {[
            {
              q: `How long does it take to learn ${data.name}?`,
              a: `${data.timeToB2} of study gets most English speakers to B2 — conversational fluency. That assumes consistent daily practice rather than occasional marathon sessions. The early stages feel slow; progress accelerates once you have ~500 words and basic grammar in place.`,
            },
            {
              q: `Is ${data.name} hard to learn?`,
              a: `${data.difficulty}. The difficulty depends more on consistency than raw ability — the learners who reach fluency fastest are those who practice a little every day rather than a lot once a week. Conjugr8 is built around short, focused sessions for exactly that reason.`,
            },
            {
              q: `What should I study first — grammar or vocabulary?`,
              a: `Both, from day one. Trying to finish grammar before touching vocabulary (or vice versa) creates a ceiling you hit quickly. High-frequency vocabulary gives you something to hang grammar rules on, and basic grammar makes vocabulary more memorable because you can use it in sentences.`,
            },
            {
              q: `How does spaced repetition help with ${data.name}?`,
              a: `Spaced repetition schedules review at the point just before you'd forget — so you're not wasting time reviewing things you already know cold, and you're not letting things you half-know slip away. For vocabulary especially, it's the difference between words that stick after a week and words you have to look up every time.`,
            },
          ].map(({ q, a }, i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
              <dt className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-1">{q}</dt>
              <dd className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── Other languages ───────────────────────────────────────────────── */}
      <section aria-label="Other languages">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">
          Also practise other Romance languages
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {otherLanguages.map(([code, lang]) => (
            <a
              key={code}
              href={`/${lang.name.toLowerCase()}-practice`}
              rel="follow index"
              className="flex items-center gap-2 rounded-lg p-3 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-colors bg-white dark:bg-gray-900"
            >
              <span className="text-xl" aria-hidden="true">
                {lang.flag}
              </span>
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {lang.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {lang.speakers} speakers
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
